/**
 * NegotiationTimeline - Chronological list of negotiation entries.
 *
 * Shows who proposed, the terms, and when, for an engaged offer's negotiation history.
 * The first entry is the initial offer terms (recorded when the offer is engaged).
 */

import { formatDistanceToNow } from 'date-fns'
import { Text } from '@/common/components/text'
import { Badge } from '@/common/components/badge'
import type { NegotiationEntry, ClosingTimeline } from '../../api/offers'
import * as styles from './NegotiationTimeline.css'

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

function formatClosingTimeline(timeline: ClosingTimeline): string {
  switch (timeline) {
    case '10_days':
      return '10 days'
    case '14_days':
      return '14 days'
    case '30_days':
      return '30 days'
    case 'flexible':
      return 'Flexible'
    default:
      return timeline
  }
}

/**
 * The entry fields the timeline reads. Any negotiation shape carrying these
 * fits, signed-in or partner link.
 */
export type NegotiationTimelineEntry = Pick<
  NegotiationEntry,
  'id' | 'role' | 'offer_price' | 'earnest_money_deposit' | 'closing_timeline' | 'notes' | 'created_at'
>

function getBadgeProps(entry: NegotiationTimelineEntry, isFirst: boolean) {
  if (isFirst) return { color: 'caution' as const, label: 'Initial Offer' }
  if (entry.role === 'agent') return { color: 'info' as const, label: 'Agent' }
  return { color: 'neutral' as const, label: 'Investor' }
}

export interface NegotiationTimelineProps {
  negotiations: readonly NegotiationTimelineEntry[]
}

export function NegotiationTimeline({ negotiations }: NegotiationTimelineProps) {
  if (negotiations.length === 0) {
    return (
      <Text variant="bodySm" color="muted">
        No negotiation history yet.
      </Text>
    )
  }

  return (
    <div className={styles.timeline}>
      {negotiations.map((entry, index) => {
        const badge = getBadgeProps(entry, index === 0)
        return (
          <div key={entry.id} className={styles.entry}>
            <div className={styles.entryHeader}>
              <Badge color={badge.color} size="sm">
                {badge.label}
              </Badge>
              <Text variant="bodyXs" color="muted">
                {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}
              </Text>
            </div>

            <div className={styles.termsList}>
              <div className={styles.termItem}>
                <Text variant="bodyXs" color="muted">Price</Text>
                <Text variant="bodySm" weight="bold">{formatCurrency(entry.offer_price)}</Text>
              </div>

              {entry.earnest_money_deposit != null && (
                <div className={styles.termItem}>
                  <Text variant="bodyXs" color="muted">EMD</Text>
                  <Text variant="bodySm">{entry.earnest_money_deposit}%</Text>
                </div>
              )}

              {entry.closing_timeline && (
                <div className={styles.termItem}>
                  <Text variant="bodyXs" color="muted">Close</Text>
                  <Text variant="bodySm">{formatClosingTimeline(entry.closing_timeline as ClosingTimeline)}</Text>
                </div>
              )}
            </div>

            {entry.notes && (
              <Text variant="bodyXs" color="muted">{entry.notes}</Text>
            )}
          </div>
        )
      })}
    </div>
  )
}
